import { useCallback, useState } from 'react';
import { useEventManagementService } from '../services/eventManagementService';
import type { PatternTestResponse } from '../../../types/backend';

export interface PatternTest {
  runTest: (eventPattern: Record<string, unknown>, testEvent: Record<string, unknown>) => Promise<void>;
  testing: boolean;
  result: PatternTestResponse | null;
  error: string | null;
  setError: (message: string | null) => void;
}

/** Tests an event pattern against a sample event through events.TestPattern. */
export function usePatternTest(customerId: string): PatternTest {
  const eventService = useEventManagementService();
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState<PatternTestResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Stable across renders so an editor re-render does not hand its buttons a new handler.
  const runTest = useCallback(async (eventPattern: Record<string, unknown>, testEvent: Record<string, unknown>) => {
    setError(null);
    if (!eventPattern || Object.keys(eventPattern).length === 0) {
      setError('Event pattern is required to test');
      return;
    }
    if (!testEvent || Object.keys(testEvent).length === 0) {
      setError('Test event is required');
      return;
    }
    setTesting(true);
    setResult(null);
    try {
      setResult(await eventService.testEventPattern(customerId, { event_pattern: eventPattern, test_event: testEvent }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to test pattern');
    } finally {
      setTesting(false);
    }
  }, [customerId, eventService]);

  return { runTest, testing, result, error, setError };
}
